import { useState, useEffect, useCallback, useRef } from "react";
import { dashboard, workouts, stats, meals, dailyTracking, cardio, photos, reports, announcementsApi } from "../api/client";

// Generic fetch hook with auto-refresh on tab focus
function useFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(0);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    lastFetchRef.current = Date.now();
    fetchFn()
      .then(setData)
      .catch((e) => { console.error("[Forge API]", e); setError(e); })
      .finally(() => setLoading(false));
  }, deps);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh when tab becomes visible (if data is stale > 2 min)
  useEffect(() => {
    const handleFocus = () => {
      if (Date.now() - lastFetchRef.current > 120000) {
        refresh();
      }
    };
    const handleVisibility = () => {
      if (!document.hidden) handleFocus();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refresh]);

  return { data, loading, error, refresh };
}

// ─── Dashboard (Today view) ────────────────────────────────
export function useDashboard() {
  return useFetch(() => dashboard.getData());
}

// ─── Assigned Workouts (Program view) ──────────────────────
export function useWorkouts() {
  const { data, loading, error, refresh } = useFetch(() => workouts.list());
  return { workouts: data?.workouts || [], loading, error, refresh };
}

// ─── Single Workout Detail ─────────────────────────────────
export function useWorkout(id) {
  return useFetch(() => workouts.get(id), [id]);
}

// ─── Client Stats (weight, measurements history) ───────────
export function useClientStats() {
  const { data, loading, error, refresh } = useFetch(() => stats.list());
  // Dashboard API returns { latestStats, activity }
  return { stats: data, loading, error, refresh };
}

// ─── Meal Plan ─────────────────────────────────────────────
export function useMealPlan() {
  const { data, loading, error, refresh } = useFetch(() => meals.getCurrentPlan());

  // Transform API meal plan to the compact format used by views
  const mealPlan = data?.hasPlan ? data.mealPlan : null;
  const apiMeals = mealPlan?.meals || [];
  const compactMeals = apiMeals.map((m) => ({
    n: m.name || m.mealTypeDisplay || "Meal",
    t: m.scheduledTime || m.time || "",
    cal: m.calories || 0,
    p: m.proteinG || 0,
    c: m.carbsG || 0,
    f: m.fatG || 0,
    foods: m.foods || m.items || [],
    desc: m.description || "",
    notes: m.notes || "",
  }));
  const macroCaps = mealPlan ? {
    cal: mealPlan.totalCalories || 0,
    p: mealPlan.totalProteinG || 0,
    c: mealPlan.totalCarbsG || 0,
    f: mealPlan.totalFatG || 0,
  } : null;

  // Supplements from assignment
  let supplementsList = [];
  if (data?.supplements) {
    try {
      supplementsList = typeof data.supplements === "string"
        ? JSON.parse(data.supplements)
        : data.supplements;
    } catch { supplementsList = []; }
  }

  return {
    meals: compactMeals,
    macroCaps,
    supplements: supplementsList,
    hasPlan: !!data?.hasPlan,
    loading,
    error,
    refresh,
  };
}

// ─── Daily Tracking (water, supplements, meals) ────────────
export function useDailyTracking(date) {
  const dateStr = date || new Date().toISOString().split("T")[0];
  const { data, loading, error, refresh } = useFetch(
    () => dailyTracking.get(dateStr), [dateStr]
  );

  const update = useCallback(async (updates) => {
    const result = await dailyTracking.update({ date: dateStr, ...updates });
    refresh();
    return result;
  }, [dateStr, refresh]);

  // Parse JSON strings from backend
  const waterGlasses = data?.waterGlasses || 0;
  let supplementChecks = {};
  let mealChecks = {};
  try {
    supplementChecks = typeof data?.supplementChecks === "string"
      ? JSON.parse(data.supplementChecks)
      : (data?.supplementChecks || {});
  } catch { supplementChecks = {}; }
  try {
    mealChecks = typeof data?.mealChecks === "string"
      ? JSON.parse(data.mealChecks)
      : (data?.mealChecks || {});
  } catch { mealChecks = {}; }

  const steps = data?.steps || 0;

  return {
    waterGlasses,
    supplementChecks,
    mealChecks,
    steps,
    loading,
    error,
    refresh,
    update,
  };
}

// ─── Progress Photos ───────────────────────────────────────
export function usePhotos() {
  const { data, loading, error, refresh } = useFetch(() => photos.list());
  return { photos: data?.photos || [], loading, error, refresh };
}

// ─── Progress Report ───────────────────────────────────────
export function useProgressReport(days = 30) {
  return useFetch(() => reports.getProgressReport(days), [days]);
}

// ─── Workout Actions (start, log, complete) ────────────────
export function useWorkoutActions() {
  const [saving, setSaving] = useState(false);

  const startWorkout = useCallback(async (assignedWorkoutId) => {
    setSaving(true);
    try {
      return await workouts.start(assignedWorkoutId);
    } finally {
      setSaving(false);
    }
  }, []);

  const logExercise = useCallback(async (data) => {
    return await workouts.logExercise(data);
  }, []);

  const completeWorkout = useCallback(async (workoutLogId, rating, notes) => {
    setSaving(true);
    try {
      return await workouts.complete(workoutLogId, rating, notes);
    } finally {
      setSaving(false);
    }
  }, []);

  return { startWorkout, logExercise, completeWorkout, saving };
}

// ─── Stats Actions (log check-in) ─────────────────────────
export function useStatsActions() {
  const [saving, setSaving] = useState(false);

  const logStats = useCallback(async (data) => {
    setSaving(true);
    try {
      return await stats.create(data);
    } finally {
      setSaving(false);
    }
  }, []);

  return { logStats, saving };
}

// ─── Check-In History (from database) ────────────────────────
export function useCheckInHistory(limit = 30) {
  const { data, loading, error, refresh } = useFetch(
    () => stats.history(limit), [limit]
  );
  return { history: data, loading, error, refresh };
}

// ─── Cardio Log ──────────────────────────────────────────────
export function useCardioLog(date) {
  const dateStr = date || new Date().toISOString().split("T")[0];
  const { data, loading, error, refresh } = useFetch(
    () => cardio.list({ date: dateStr }), [dateStr]
  );

  const logSession = useCallback(async (sessionData) => {
    const result = await cardio.log({ date: dateStr, ...sessionData });
    refresh();
    return result;
  }, [dateStr, refresh]);

  return {
    sessions: data?.logs || [],
    loading,
    error,
    refresh,
    logSession,
  };
}

export function useCardioHistory(days = 30) {
  const { data, loading, error, refresh } = useFetch(
    () => cardio.list({ days }), [days]
  );
  return { logs: data?.logs || [], loading, error, refresh };
}

// ─── Transform API workout to display format ───────────────
// Converts API exercise format to the compact display format used by views
export function transformWorkout(apiWorkout, index) {
  if (!apiWorkout) return null;
  return {
    id: apiWorkout.id,
    d: index != null ? index + 1 : apiWorkout.id,
    t: apiWorkout.workoutName || apiWorkout.name || "Workout",
    m: apiWorkout.durationMinutes || 60,
    status: apiWorkout.status,
    scheduledDate: apiWorkout.scheduledDate,
    notes: apiWorkout.notes,
    rest: false,
    w: [],
    x: (apiWorkout.exercises || []).map((ex) => ({
      id: ex.exerciseId,
      n: ex.name,
      s: ex.sets && ex.reps ? `${ex.sets}x${ex.reps}` : "",
      ns: ex.sets || 3,
      reps: ex.reps,
      restSeconds: ex.restSeconds || 90,
      tg: null,
      c: ex.instructions ? [ex.instructions] : [],
      p: ex.notes || null,
      muscleGroup: ex.muscleGroup,
      equipment: ex.equipmentNeeded,
    })),
  };
}

// Transform array of API workouts, inserting rest days at configured positions
export function transformWorkouts(apiWorkouts) {
  const transformed = (apiWorkouts || []).map((w) => transformWorkout(w, 0)).filter(Boolean);
  if (transformed.length === 0) return [];

  // Insert rest days at positions 7 and 14 (from config: restDays)
  const restDays = [7, 14];
  const result = [];
  let workoutIdx = 0;

  // Calculate total days: insert rest at 7,14 for programs up to 12 workouts.
  // For programs >12, extend to fit all workouts plus applicable rests.
  const totalDays = transformed.length <= 6 ? 7
    : transformed.length <= 12 ? 14
    : (() => {
        let days = 0, placed = 0;
        while (placed < transformed.length) {
          days++;
          if (!restDays.includes(days)) placed++;
        }
        return days;
      })();

  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    if (restDays.includes(dayNum)) {
      result.push({ d: dayNum, t: "Rest", m: 0, rest: true, w: [], x: [] });
    } else if (workoutIdx < transformed.length) {
      result.push({ ...transformed[workoutIdx], d: dayNum });
      workoutIdx++;
    }
  }

  return result;
}

// ─── Announcements ───────────────────────────────────────
export function useAnnouncements() {
  const { data, loading, error, refresh } = useFetch(() => announcementsApi.getUnread());
  return {
    announcements: data?.announcements || [],
    loading,
    error,
    refresh,
  };
}
